import type { PlanningAlgorithm } from '@/engine/PlanningAlgorithm/PlanningAlgorithm';
import { DisposablesBasket } from '@/engine/Utilities/DisposablesBasket';
import { TYPES } from '@/engine/ioc/types';
import { useInjection } from 'inversify-react';
import React, { useEffect } from 'react';
import type * as DT from '@/engine/PlanningAlgorithm/DataTypes';
import { IdentifiersListTuner } from '@/engine/PlanningAlgorithm/Tuners/IdentifiersListTuner';
import { CORE_TYPES } from '@/engine/app/modules/core';
import type { ICommand, ICommandProcessor } from '@/engine/app/modules/core/interfaces/Command';
import type { ITuneCommandFactory } from '@/engine/PlanningAlgorithm/Tuners/commands/interfaces';

export interface IIdentifierTuner {
  doTune: (value: string) => void;
}

export const useIdentifiersListTuner = (
  descriptor: string
): {
  value: string | undefined;
  valuesList: Array<string>;
  tuner: IIdentifierTuner;
} => {
  const planningAlgorithm = useInjection<PlanningAlgorithm>(TYPES.PlanningAlgorithm);
  const tuneCommandsFactory = useInjection<ITuneCommandFactory<ICommand, IdentifiersListTuner, string>>(
    TYPES.IdentifierTuneCommandsFactory
  );
  const commandProcessor = useInjection<ICommandProcessor>(CORE_TYPES.CommandProcessor);
  const [value, setValue] = React.useState<string | undefined>(undefined);
  const [valuesList, setValuesList] = React.useState<Array<string>>(['']);
  const [tuner, setTuner] = React.useState<IIdentifierTuner>({
    doTune: (): void => {
      return;
    }
  });

  useEffect(() => {
    const algoTuner = new IdentifiersListTuner(planningAlgorithm);

    const valueSetter = (val: string): void => {
      const command: ICommand = tuneCommandsFactory.createCommand(algoTuner, descriptor, val);
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- //TODO: [chebotarev] fix eslint no-floating-promises
      commandProcessor.execute(command);
    };

    const onValueChanged = (tuneValue?: DT.StringT): void => {
      setValue(tuneValue?.value);
    };

    const onValuesListChanged = (tuneValue?: DT.IdentifiersList): void => {
      if (!tuneValue) {
        throw new Error('tuneValue expected to be valid here');
      }
      setValuesList(tuneValue.value);
    };

    const subscriptions: DisposablesBasket = new DisposablesBasket();
    subscriptions.add(planningAlgorithm.addOnChangedCallbackFor<DT.StringT>(descriptor, onValueChanged));
    subscriptions.add(
      planningAlgorithm.addOnChangedCallbackFor<DT.IdentifiersList>(
        algoTuner.getListName(descriptor),
        onValuesListChanged
      )
    );

    setValue(algoTuner.getValueSafe(descriptor));
    setValuesList(algoTuner.getValuesList(descriptor));
    setTuner({ doTune: valueSetter });

    return (): void => subscriptions.dispose();
  }, [planningAlgorithm, descriptor, tuneCommandsFactory, commandProcessor]);

  return { value: value, valuesList: valuesList, tuner: tuner };
};
